import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Linkedin, Award, Users as UsersIcon, Calendar, X } from 'lucide-react';
import { benefits } from '../data/benefits';
import { portfolios } from '../data/portfolios';
import { mentors } from '../data/mentors';
import { programs } from '../data/programs';
import { portfolioItems } from '../data/portfolio';
import { partners } from '../data/partners';

const HomePage = ({
  setCurrentPage,
  currentPortfolio,
  setCurrentPortfolio,
  currentMentor,
  setCurrentMentor,
  registrations
}) => {
  const verifiedCount = registrations.filter(r => r.status === 'verified').length;
  const totalStudents = verifiedCount; // Real verified registrations from database

  // Animated counter state
  const [animatedStudents, setAnimatedStudents] = useState(0);
  const [animatedPrograms, setAnimatedPrograms] = useState(0);
  const [animatedMentors, setAnimatedMentors] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsRef = useRef(null);

  // Portfolio detail modal state
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState(null);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Partner detail modal state
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showPartnerModal, setShowPartnerModal] = useState(false);

  const handlePortfolioClick = (item) => {
    setSelectedPortfolioItem(item);
    setShowPortfolioModal(true);
    setCurrentImageIndex(0); // Reset to first image
  };

  const handlePartnerClick = (partner) => {
    setSelectedPartner(partner);
    setShowPartnerModal(true);
  };

  const closePortfolioModal = () => {
    setShowPortfolioModal(false);
    setSelectedPortfolioItem(null);
    setCurrentImageIndex(0);
  };

  const closePartnerModal = () => {
    setShowPartnerModal(false);
    setSelectedPartner(null);
  };

  const nextImage = () => {
    if (selectedPortfolioItem?.images) {
      setCurrentImageIndex((prev) =>
        (prev + 1) % selectedPortfolioItem.images.length
      );
    }
  };

  const prevImage = () => {
    if (selectedPortfolioItem?.images) {
      setCurrentImageIndex((prev) =>
        (prev - 1 + selectedPortfolioItem.images.length) % selectedPortfolioItem.images.length
      );
    }
  };

  // Counter animation effect
  useEffect(() => {
    const animateCounter = (target, setter, duration = 2000) => {
      const steps = 60;
      const increment = target / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setter(target);
          clearInterval(timer);
        } else {
          setter(Math.floor(current));
        }
      }, duration / steps);
      return timer;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCounter(totalStudents, setAnimatedStudents);
          animateCounter(programs.length, setAnimatedPrograms);
          animateCounter(mentors.length, setAnimatedMentors);
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }


      const currentRef = statsRef.current;
      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      };
    }, [hasAnimated, totalStudents]);

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 py-20 relative overflow-hidden transition-colors">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-sky-300 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-6 leading-tight">
                Platform Pembelajaran Data Science & AI
              </h1>
              <p className="text-lg text-blue-50 mb-8 leading-relaxed">
                Bangun pemahaman dan keterampilan Data Science & AI melalui pembelajaran terstruktur dan aplikatif.
              </p>
              <button
                onClick={() => setCurrentPage('program')}
                className="bg-white text-blue-600 px-8 py-3 rounded-xl text-lg hover:bg-blue-50 transition font-bold shadow-lg"
              >
                Jelajahi Sekarang
              </button>
            </div>
            <div className="flex justify-center">
              <div className="w-full h-64 md:h-80 bg-gradient-to-br from-blue-100 to-sky-200 rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                <img
                  src="/images/hero/data-science.gif"
                  alt="Data Science Animation"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to SVG icon if GIF not found
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <div class="w-full h-full flex items-center justify-center">
                        <svg class="w-32 h-32 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                    `;
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section ref={statsRef} className="py-16 bg-blue-50 border-b-2 border-blue-200 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center transform hover:scale-110 transition-transform duration-300">
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-blue-100">
                <div className="text-5xl font-bold text-blue-600 mb-2 animate-pulse-slow">
                  {animatedStudents > 0 ? `${animatedStudents}+` : animatedStudents}
                </div>
                <p className="text-blue-700 font-semibold">Siswa Terdaftar</p>
              </div>
            </div>
            <div className="text-center transform hover:scale-110 transition-transform duration-300">
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-blue-100">
                <div className="text-5xl font-bold text-blue-600 mb-2 animate-pulse-slow">
                  {animatedPrograms}
                </div>
                <p className="text-blue-700 font-semibold">Program Kelas</p>
              </div>
            </div>
            <div className="text-center transform hover:scale-110 transition-transform duration-300">
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-blue-100">
                <div className="text-5xl font-bold text-blue-600 mb-2 animate-pulse-slow">
                  {animatedMentors}
                </div>
                <p className="text-blue-700 font-semibold">Mentor Ahli</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12 text-blue-900">Keuntungan Memilih AIXpert</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 rounded-2xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition bg-blue-50">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold mb-2 text-blue-900">{benefit.title}</h3>
                <p className="text-blue-700">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section - ISTN & Veloct */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-blue-100 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-blue-200 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Award className="w-4 h-4 inline mr-2" />
              Partner Terpercaya
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-blue-900 mb-4">
              Institusi & Perusahaan Partner Kami
            </h2>
            <p className="text-blue-700 max-w-2xl mx-auto">
              Bekerja sama dengan institusi terkemuka dan perusahaan teknologi terdepan untuk memberikan pelatihan berkualitas tinggi
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {partners.map((partner) => (
              <div
                key={partner.id}
                onClick={() => handlePartnerClick(partner)}
                className="bg-white rounded-2xl overflow-hidden border-2 border-blue-300 hover:shadow-2xl hover:border-blue-500 transition group cursor-pointer"
              >
                {/* Logo */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 flex items-center justify-center min-h-32">
                  <img
                    src={partner.image}
                    alt={partner.name}
                    className="h-20 w-auto object-contain"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%2393c5fd" width="100" height="100" rx="10"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" font-weight="bold" fill="%231e3a8a"%3E' + encodeURIComponent(partner.name) + '%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>

                {/* Content */}
                <div className="p-8">
                  <div className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                    {partner.role}
                  </div>
                  <h3 className="text-2xl font-bold text-blue-900 mb-2">
                    {partner.name}
                  </h3>
                  <p className="text-blue-600 font-semibold mb-4 text-sm">
                    {partner.fullName}
                  </p>
                  <p className="text-blue-700 mb-4">
                    {partner.description}
                  </p>
                  <p className="text-blue-600 text-sm leading-relaxed border-l-4 border-blue-400 pl-4 mb-4">
                    {partner.shortDescription}
                  </p>
                  <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-semibold text-sm">
                    Pelajari Lebih Lanjut →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="py-20 bg-blue-50 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12 text-blue-900">Portfolio Hasil Belajar di AIXpert</h2>
          <div className="relative">
            <div className="overflow-hidden">
              <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${currentPortfolio * 100}%)` }}>
                {portfolios.map((portfolio, index) => (
                  <div key={index} className="min-w-full md:min-w-[33.333%] px-4">
                    <div className="bg-white rounded-2xl border-2 border-blue-200 overflow-hidden hover:shadow-xl transition">
                      <div className="h-48 bg-gradient-to-br from-cream-200 to-cream-300 flex items-center justify-center text-6xl">
                        {portfolio.image}
                      </div>
                      <div className="p-6">
                        <p className="text-sm text-blue-600 font-semibold mb-2">{portfolio.author}</p>
                        <h3 className="text-lg font-bold text-blue-900">{portfolio.title}</h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => setCurrentPortfolio(Math.max(0, currentPortfolio - 1))}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-blue-700 text-white rounded-full p-2 shadow-lg hover:bg-blue-800 disabled:opacity-50 transition"
              disabled={currentPortfolio === 0}
            >
              <ChevronLeft />
            </button>
            <button
              onClick={() => setCurrentPortfolio(Math.min(portfolios.length - 3, currentPortfolio + 1))}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-blue-700 text-white rounded-full p-2 shadow-lg hover:bg-blue-800 disabled:opacity-50 transition"
              disabled={currentPortfolio >= portfolios.length - 3}
            >
              <ChevronRight />
            </button>
          </div>
        </div>
      </section>

      {/* VeloCT Portfolio/Experience Section */}
      <section className="py-20 bg-white transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Award className="w-4 h-4 inline mr-2" />
              Trusted Experience
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-blue-900 mb-4">
              Pengalaman VeloCT
            </h2>
            <p className="text-blue-600 max-w-2xl mx-auto">
              VeloCT telah dipercaya berbagai instansi pemerintah dan korporasi untuk memberikan pelatihan Data Science & AI berkualitas
            </p>
          </div>

          {/* Portfolio Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {portfolioItems.map((item) => (
              <div
                key={item.id}
                onClick={() => handlePortfolioClick(item)}
                className="bg-blue-50 rounded-2xl overflow-hidden border-2 border-blue-200 hover:border-blue-400 hover:shadow-xl transition group cursor-pointer"
              >
                {/* Image */}
                <div className="relative h-48 bg-gradient-to-br from-blue-200 to-blue-300 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%2393c5fd" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="16" fill="%231e3a8a"%3E' + encodeURIComponent(item.category) + '%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  {/* Gradient Overlay for Badge */}
                  <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  {/* Category Badge */}
                  <div className="absolute bottom-3 right-3 bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-semibold shadow-lg">
                    {item.category}
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-blue-900 mb-3 line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-blue-600 mb-4 line-clamp-2">
                    {item.description}
                  </p>

                  {/* Meta Info */}
                  <div className="space-y-2 text-xs text-blue-700">
                    <div className="flex items-center gap-2">
                      <UsersIcon className="w-4 h-4 text-blue-500" />
                      <span className="font-semibold">{item.participants}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span>{item.duration} • {item.year}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="mt-12 text-center">
            <button
              onClick={() => setCurrentPage('about')}
              className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition font-bold shadow-lg inline-flex items-center gap-2"
            >
              Lihat Lebih Lanjut
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Mentors Section */}
      <section className="py-20 bg-blue-50 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12 text-blue-900">Mentor dan Para Ahli di Bidangnya</h2>
          <div className="relative">
            <div className="overflow-hidden">
              <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${currentMentor * 100}%)` }}>
                {mentors.map((mentor, index) => (
                  <div key={index} className="min-w-full md:min-w-[33.333%] px-4">
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 text-center hover:shadow-lg transition">
                      {mentor.image ? (
                        <img
                          src={mentor.image}
                          alt={mentor.name}
                          className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-blue-600"
                        />
                      ) : (
                        <div className="w-32 h-32 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full mx-auto mb-4 flex items-center justify-center text-white">
                          <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      <h3 className="text-xl font-bold mb-2 text-blue-900">{mentor.name}</h3>
                      <p className="text-blue-700 font-semibold mb-2">{mentor.role}</p>
                      <p className="text-blue-600 text-sm mb-1">{mentor.description}</p>
                      <p className="text-blue-600 text-sm font-medium">{mentor.university}</p>
                      <a
                        href={mentor.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 bg-[#0077B5] text-white px-6 py-2 rounded-xl hover:bg-[#006399] transition font-semibold inline-flex items-center gap-2"
                      >
                        <Linkedin size={18} />
                        LinkedIn Profile
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => setCurrentMentor(Math.max(0, currentMentor - 1))}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-blue-700 text-white rounded-full p-2 shadow-lg hover:bg-blue-800 disabled:opacity-50 transition"
              disabled={currentMentor === 0}
            >
              <ChevronLeft />
            </button>
            <button
              onClick={() => setCurrentMentor(Math.min(mentors.length - 3, currentMentor + 1))}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-blue-700 text-white rounded-full p-2 shadow-lg hover:bg-blue-800 disabled:opacity-50 transition"
              disabled={currentMentor >= mentors.length - 3}
            >
              <ChevronRight />
            </button>
          </div>
        </div>
      </section>

      {/* Portfolio Detail Modal */}
      {showPortfolioModal && selectedPortfolioItem && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4" onClick={closePortfolioModal}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-700 to-blue-600 text-white px-6 py-4 flex justify-between items-center rounded-t-2xl">
              <h2 className="text-2xl font-bold">{selectedPortfolioItem.title}</h2>
              <button
                onClick={closePortfolioModal}
                className="text-white hover:bg-blue-800 rounded-full p-2 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Image Gallery/Carousel */}
              <div className="relative h-80 bg-gradient-to-br from-blue-200 to-blue-300 rounded-xl overflow-hidden mb-6">
                {selectedPortfolioItem.images && selectedPortfolioItem.images.length > 0 ? (
                  <>
                    {/* Main Image */}
                    <img
                      src={selectedPortfolioItem.images[currentImageIndex]}
                      alt={`${selectedPortfolioItem.title} - ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect fill="%2393c5fd" width="800" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="20" fill="%231e3a8a"%3E' + encodeURIComponent(selectedPortfolioItem.category) + '%3C/text%3E%3C/svg%3E';
                      }}
                    />

                    {/* Navigation Arrows (only show if multiple images) */}
                    {selectedPortfolioItem.images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-blue-700 p-3 rounded-full shadow-lg transition"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-blue-700 p-3 rounded-full shadow-lg transition"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>

                        {/* Image Counter */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-semibold">
                          {currentImageIndex + 1} / {selectedPortfolioItem.images.length}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  /* Fallback to single image if no images array */
                  <img
                    src={selectedPortfolioItem.image}
                    alt={selectedPortfolioItem.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect fill="%2393c5fd" width="800" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="20" fill="%231e3a8a"%3E' + encodeURIComponent(selectedPortfolioItem.category) + '%3C/text%3E%3C/svg%3E';
                    }}
                  />
                )}

                {/* Gradient Overlay for Badge */}
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none"></div>
                {/* Category Badge */}
                <div className="absolute bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full font-semibold shadow-lg">
                  {selectedPortfolioItem.category}
                </div>
              </div>

              {/* Thumbnail Strip (if multiple images) */}
              {selectedPortfolioItem.images && selectedPortfolioItem.images.length > 1 && (
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                  {selectedPortfolioItem.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition ${
                        index === currentImageIndex
                          ? 'border-blue-600 ring-2 ring-blue-300'
                          : 'border-blue-200 hover:border-blue-400'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%2393c5fd" width="100" height="100"/%3E%3C/svg%3E';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Info Grid */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200 text-center">
                  <UsersIcon className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-blue-600 font-medium mb-1">Peserta</p>
                  <p className="text-lg font-bold text-blue-900">{selectedPortfolioItem.participants}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200 text-center">
                  <Calendar className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-blue-600 font-medium mb-1">Durasi</p>
                  <p className="text-lg font-bold text-blue-900">{selectedPortfolioItem.duration}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200 text-center">
                  <Award className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-blue-600 font-medium mb-1">Tahun</p>
                  <p className="text-lg font-bold text-blue-900">{selectedPortfolioItem.year}</p>
                </div>
              </div>

              {/* Description */}
              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                <h3 className="text-lg font-bold text-blue-900 mb-3">Deskripsi Program</h3>
                <p className="text-blue-700 leading-relaxed">
                  {selectedPortfolioItem.description}
                </p>
              </div>

              {/* Additional Info - Edit this section in portfolio.js if you want to add more details */}
              {selectedPortfolioItem.details && (
                <div className="mt-6 bg-white rounded-xl p-6 border-2 border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Detail Tambahan</h3>
                  <div className="text-blue-700 leading-relaxed whitespace-pre-line">
                    {selectedPortfolioItem.details}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={closePortfolioModal}
                  className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition font-bold"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partner Detail Modal */}
      {showPartnerModal && selectedPartner && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4" onClick={closePartnerModal}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-700 to-blue-600 text-white px-6 py-6 flex justify-between items-center rounded-t-2xl">
              <div>
                <h2 className="text-3xl font-bold">{selectedPartner.name}</h2>
                <p className="text-blue-100 text-sm mt-1">{selectedPartner.fullName}</p>
              </div>
              <button
                onClick={closePartnerModal}
                className="text-white hover:bg-blue-800 rounded-full p-2 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              {/* Logo */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8 flex items-center justify-center mb-6">
                <img
                  src={selectedPartner.image}
                  alt={selectedPartner.name}
                  className="h-24 w-auto object-contain"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%2393c5fd" width="100" height="100" rx="10"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" font-weight="bold" fill="%231e3a8a"%3E' + encodeURIComponent(selectedPartner.name) + '%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>

              {/* Role Badge */}
              <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                {selectedPartner.role}
              </div>

              {/* Full Description */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-blue-900 mb-4">Tentang Partner Kami</h3>
                <p className="text-blue-700 leading-relaxed whitespace-pre-line text-justify">
                  {selectedPartner.fullDescription}
                </p>
              </div>

              {/* Achievements */}
              {selectedPartner.achievements && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-blue-900 mb-4">Pencapaian & Keunggulan</h3>
                  <div className="grid gap-3">
                    {selectedPartner.achievements.map((achievement, index) => (
                      <div key={index} className="flex items-start gap-3 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
                        <div className="text-blue-600 mt-1">✓</div>
                        <p className="text-blue-700 font-medium">{achievement}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Info */}
              {selectedPartner.contact && (
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200 mb-8">
                  <h3 className="text-lg font-bold text-blue-900 mb-4">Hubungi Kami</h3>
                  <div className="space-y-3">
                    {selectedPartner.contact.email && (
                      <div className="flex items-center gap-3">
                        <span className="text-blue-600 font-semibold">Email:</span>
                        <a href={`mailto:${selectedPartner.contact.email}`} className="text-blue-600 hover:text-blue-800 underline">
                          {selectedPartner.contact.email}
                        </a>
                      </div>
                    )}
                    {selectedPartner.contact.website && (
                      <div className="flex items-center gap-3">
                        <span className="text-blue-600 font-semibold">Website:</span>
                        <a href={selectedPartner.contact.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                          Kunjungi Website
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="flex justify-end">
                <button
                  onClick={closePartnerModal}
                  className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition font-bold"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
